import { Button, Link, Select, SelectOption } from "@clockwise/design-system";
import {
  useGatewayMutation,
  useGatewayQuery,
} from "@clockwise/web-commons/src/network/apollo/gateway-provider";
import { Dialog, DialogActions, DialogContent, DialogTitle } from "@material-ui/core";
import { compact, sortBy } from "lodash";
import React from "react";
import toast from "react-hot-toast";
import { TrackingEvents, track } from "../../../util/analytics.util";
import { logger } from "../../../util/logger.util";
import {
  PrimaryTeamSelectDocument,
  UpdatePrimaryTeamDocument,
} from "./__generated__/PrimaryTeamSelect.v2.generated";

export const PrimaryTeamSelect = ({ pageTeamId }: { pageTeamId?: string }) => {
  const { data, loading } = useGatewayQuery(PrimaryTeamSelectDocument);
  const [updatePrimaryTeam, { loading: saving }] = useGatewayMutation(UpdatePrimaryTeamDocument, {
    refetchQueries: [PrimaryTeamSelectDocument],
  });
  const userTeams = compact(sortBy(data?.currentUser?.teams ?? [], "name"));
  const primaryTeamId = data?.currentUser?.teams.find((team) => team?.isPrimary)?.id ?? "";

  const [modalOpen, setShowPrimaryTeamModal] = React.useState(false);
  const [selectedPrimaryTeamId, setSelectedPrimaryTeamId] = React.useState(primaryTeamId);

  const onChangePrimary = () => {
    setShowPrimaryTeamModal(true);
    track(TrackingEvents.TEAMS.CLICKED_CHANGE_PRIMARY_TEAM);
  };

  const onCloseModal = () => {
    setShowPrimaryTeamModal(false);
    setSelectedPrimaryTeamId(primaryTeamId);
    track(TrackingEvents.TEAMS.CLOSE_CHANGE_PRIMARY_TEAM_MODAL);
  };

  const onSavePrimaryTeam = () => {
    void updatePrimaryTeam({
      variables: { teamId: selectedPrimaryTeamId },
      refetchQueries: [PrimaryTeamSelectDocument],
      awaitRefetchQueries: true,
      onCompleted: () => {
        toast.success("Updated your primary team");
        track(TrackingEvents.TEAMS.CHANGED_PRIMARY_TEAM, {
          selectedPrimaryTeamId,
          prevPrimaryTeamId: primaryTeamId,
        });
        setShowPrimaryTeamModal(false);
      },
      onError: (error) => {
        toast.error("Failed to update your primary team");
        logger.error("Failed to update your primary team", error);
      },
    });
  };

  if (!primaryTeamId) {
    return null;
  }

  const isOnPrimaryTeamPage = primaryTeamId === pageTeamId;

  return (
    <>
      <span>
        {isOnPrimaryTeamPage ? (
          <span>
            This is your primary team. <Link onClick={onChangePrimary}>Change</Link>
          </span>
        ) : (
          <Link onClick={onChangePrimary}>Change primary team</Link>
        )}
      </span>
      <Dialog open={modalOpen} onClose={onCloseModal}>
        <DialogTitle>
          <div className="cw-heading-xl">Set your primary team</div>
        </DialogTitle>
        <DialogContent className="cw-py-3">
          <div className="cw-space-y-3">
            <div className="cw-body-base cw-text-muted">
              Your primary team is your default team in Clockwise. You will receive proactive alerts
              about your primary teammates' availability and capacity.
            </div>
            <Select
              value={selectedPrimaryTeamId}
              onChange={setSelectedPrimaryTeamId}
              label="Primary team"
              name="Primary team"
              disabled={loading || saving}
            >
              {userTeams.map(({ id, name }) => (
                <SelectOption
                  cw-id="team-in-teampicker"
                  key={`team-select-${id}-${name}`}
                  value={id}
                >
                  {name}
                </SelectOption>
              ))}
            </Select>
          </div>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={onCloseModal} disabled={saving}>
            Cancel
          </Button>
          <Button
            sentiment="positive"
            onClick={onSavePrimaryTeam}
            color="primary"
            disabled={saving}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
